import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject } from "@angular/core";
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { HttpRequestResponse } from "../../models/http-request-response.model";
import { HttpRequestService } from "../../services/http-request.service";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { MatFormField, MatLabel, MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";

interface MedicoCambio {
    codguid: string;
    nome: string;
    cognome: string;
    codfis: string;
    citta: string;
    regione: string;
    dataNascita: string | null;
}

@Component({
    selector: "ev-cambio-medico",
    templateUrl: "./cambio-medico.component.html",
    styleUrls: ["./cambio-medico.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatDialogTitle, CdkScrollable, MatDialogContent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatButton, MatDialogActions]
})
export class EvCambioMedicoComponent implements OnDestroy {
    private http = inject(HttpRequestService);
    private dialogRef = inject<MatDialogRef<EvCambioMedicoComponent>>(MatDialogRef);
    private changeDetector = inject(ChangeDetectorRef);

    form = new FormGroup({
        cognome: new FormControl("", [Validators.required, Validators.maxLength(50)]),
        nome: new FormControl("", [Validators.maxLength(50)])
    });

    medici: MedicoCambio[] = [];
    medicoSelezionato: MedicoCambio | null = null;
    searched = false;
    loading = false;
    sending = false;
    errorMessage = "";
    successMessage = "";
    private subscriptions: Subscription[] = [];

    constructor() {
        this.subscriptions.push(this.form.valueChanges.subscribe(() => {
            this.medici = [];
            this.medicoSelezionato = null;
            this.searched = false;
            this.errorMessage = "";
        }));
    }

    search(): void {
        if (this.form.invalid || this.loading || this.sending) {
            this.form.markAllAsTouched();
            return;
        }

        const nome = (this.form.controls.nome.value || "").trim();
        const cognome = (this.form.controls.cognome.value || "").trim();
        if (!cognome) {
            this.errorMessage = "Inserire il cognome del medico";
            return;
        }

        this.loading = true;
        this.searched = false;
        this.errorMessage = "";
        this.medici = [];
        this.medicoSelezionato = null;
        this.changeDetector.detectChanges();

        this.subscriptions.push(this.http.get("/cambio-medico/medici", { nome, cognome }, {}, false, false).subscribe({
            next: response => {
                this.loading = false;
                if ((this.form.controls.nome.value || "").trim() !== nome ||
                    (this.form.controls.cognome.value || "").trim() !== cognome) {
                    this.changeDetector.detectChanges();
                    return;
                }
                if (HttpRequestResponse.isSuccessResponse(response)) {
                    if (Array.isArray(response.data?.medici)) {
                        this.medici = response.data.medici;
                        this.searched = true;
                    } else {
                        this.errorMessage = "Risposta della ricerca non valida";
                    }
                } else {
                    this.errorMessage = response.message;
                }
                this.changeDetector.detectChanges();
            },
            error: error => {
                this.loading = false;
                this.errorMessage = error.error?.message || "Ricerca dei medici non riuscita";
                this.changeDetector.detectChanges();
            }
        }));
    }

    selectDoctor(doctor: MedicoCambio): void {
        this.medicoSelezionato = doctor;
        this.errorMessage = "";
        this.changeDetector.detectChanges();
    }

    send(): void {
        if (!this.medicoSelezionato || this.sending) return;
        this.sending = true;
        this.errorMessage = "";
        this.changeDetector.detectChanges();

        this.subscriptions.push(this.http.post("/cambio-medico/richiesta", {
            codguidMedico: this.medicoSelezionato.codguid
        }, {}, false).subscribe({
            next: response => {
                this.sending = false;
                if (HttpRequestResponse.isSuccessResponse(response)) {
                    this.successMessage = "Richiesta di cambio medico inviata con successo.";
                } else {
                    this.errorMessage = response.message;
                }
                this.changeDetector.detectChanges();
            },
            error: error => {
                this.sending = false;
                this.errorMessage = error.error?.message || "Invio della richiesta non riuscito";
                this.changeDetector.detectChanges();
            }
        }));
    }

    close(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
